<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import DashboardBox from "@/Components/Common/Helpers/DashboardBox.vue";
import {computed} from "vue";
import {useHelpers, useTenantTextRules} from "@/helpers";
import {Link, usePage} from "@inertiajs/inertia-vue3";
import FolderPlus from "@/Components/Common/Svg/FolderPlus.vue";
import CartOutline from "@/Components/Common/Svg/CartOutline.vue";
import ApexStackedColumn from "@/Components/Common/Helpers/ApexStackedColumn.vue";
import ApexDonut from "@/Components/Common/Helpers/ApexDonut.vue";

const props = defineProps({
  lateWorkOrders: Array,
  currentWorkOrders: Array,
  graphs: Object
});

const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const currentUser = usePage().props.value.auth.user;
const navigationVisibility = usePage().props.value?.app?.navigationVisibility || {};
const isMenuVisible = (key) => navigationVisibility?.[key] !== false;

const orderName = (order) => {
  let name = `${order.company.client} - ${order.user.firstname}`;
  if (order.user.lastname) {
    name += ` ${order.user.lastname}`;
  }

  return name;
};

const compareMonths = (dataPoint) => {
  if (dataPoint.previous_30 === 0) {
    return 0;
  }
  return Math.round(((dataPoint.last_30 - dataPoint.previous_30) / dataPoint.previous_30) * 100);
}
const createdProjectsLabel = computed(() =>
  resolveTextRule('dashboard.cards.created_projects', 'Created Projects', 'dashboard.cards.created_projects')
);
const createdPurchaseOrdersLabel = computed(() =>
  resolveTextRule('dashboard.cards.created_purchase_orders', 'Created Purchase Orders', 'dashboard.cards.created_purchase_orders')
);
const closedProjectsLabel = computed(() =>
  resolveTextRule('dashboard.cards.closed_projects', 'Closed Projects', 'dashboard.cards.closed_projects')
);
const closedPurchaseOrdersLabel = computed(() =>
  resolveTextRule('dashboard.cards.closed_purchase_orders', 'Closed Purchase Orders', 'dashboard.cards.closed_purchase_orders')
);
const projectsBreakdownLabel = computed(() =>
  resolveTextRule('dashboard.charts.projects_breakdown', 'Projects Breakdown', 'dashboard.charts.projects_breakdown')
);
const purchaseOrdersBreakdownLabel = computed(() =>
  resolveTextRule('dashboard.charts.purchase_orders_breakdown', 'Purchase Orders Breakdown', 'dashboard.charts.purchase_orders_breakdown')
);
const projectsDonutTitle = computed(() =>
  resolveTextRule('dashboard.charts.projects_donut_title', 'Projects', 'dashboard.charts.projects_donut_title')
);
const purchaseOrdersDonutTitle = computed(() =>
  resolveTextRule('dashboard.charts.purchase_orders_donut_title', 'Purchase Orders', 'dashboard.charts.purchase_orders_donut_title')
);
const openProjectsSeriesLabel = computed(() =>
  resolveTextRule('dashboard.charts.projects_open_series', 'Open Projects', 'dashboard.charts.projects_open_series')
);
const shippedProjectsSeriesLabel = computed(() =>
  resolveTextRule('dashboard.charts.projects_shipped_series', 'Shipped Projects', 'dashboard.charts.projects_shipped_series')
);
const openPurchaseOrdersSeriesLabel = computed(() =>
  resolveTextRule('dashboard.charts.purchase_orders_open_series', 'Open Purchase Orders', 'dashboard.charts.purchase_orders_open_series')
);
const shippedPurchaseOrdersSeriesLabel = computed(() =>
  resolveTextRule('dashboard.charts.purchase_orders_shipped_series', 'Shipped Purchase Orders', 'dashboard.charts.purchase_orders_shipped_series')
);
const projectsDonutLabels = computed(() => ([
  resolveTextRule('dashboard.charts.projects_open_slice', 'Open', 'dashboard.charts.projects_open_slice'),
  resolveTextRule('dashboard.charts.projects_shipped_slice', 'Shipped', 'dashboard.charts.projects_shipped_slice'),
]));
const purchaseOrdersDonutLabels = computed(() => ([
  resolveTextRule('dashboard.charts.purchase_orders_open_slice', 'Open', 'dashboard.charts.purchase_orders_open_slice'),
  resolveTextRule('dashboard.charts.purchase_orders_shipped_slice', 'Shipped', 'dashboard.charts.purchase_orders_shipped_slice'),
]));
const quickLinkProjectManagementLabel = computed(() =>
  resolveTextRule('dashboard.quick_links.project_management', 'Project Management', 'dashboard.quick_links.project_management')
);
const quickLinkPurchaseOrderLabel = computed(() =>
  resolveTextRule('dashboard.quick_links.purchase_orders', 'Purchase Order', 'dashboard.quick_links.purchase_orders')
);

</script>

<template>
  <AppLayout title="Dashboard">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Dashboard
      </h2>
    </template>

    <div class="w-full py-6 sm:px-4 lg:px-6">
      <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4 lg:grid-cols-2 sm:grid-cols-1 xs:grid-cols-1">
        <div class="col-span-2 xl:col-span-1">
          <div class="bg-white shadow sm:rounded-md p-3 mb-3">
            <div class="grid gap-2 grid-cols-12">
              <div class="col-span-3">
                <div class="bg-teal-50 rounded-lg p-4 w-20 h-20">
                  <FolderPlus class="fill-emerald-400"/>
                </div>
              </div>
              <div class="col-span-9">
                <div class="text-gray-500 text-sm">{{ createdProjectsLabel }} <span>(last 30 days)</span></div>
                <div class="text-gray-900 text-2xl font-bold" v-text="props.graphs.open.project.total.last_30"></div>
                <div class="text-gray-500 text-sm">
                  <span class="font-bold"
                        :class="{'text-teal-500': compareMonths(props.graphs.open.project.total) > 0, 'text-red-500': compareMonths(props.graphs.open.project.total) < 0}"><span
                    class="fa"
                    :class="{'fa-arrow-up': compareMonths(props.graphs.open.project.total) > 0, 'fa-arrow-down': compareMonths(props.graphs.open.project.total) < 0}"></span><span
                    v-text="compareMonths(props.graphs.open.project.total)"></span>%</span>
                  vs last month
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white shadow sm:rounded-md p-3 mb-3">
            <div class="grid gap-2 grid-cols-12 mt-2">
              <div class="col-span-3">
                <div class="bg-teal-50 rounded-lg p-4 w-20 h-20">
                  <CartOutline class="fill-emerald-400 w-full h-full"/>
                </div>
              </div>
              <div class="col-span-9">
                <div class="text-gray-500 text-sm font-normal">{{ createdPurchaseOrdersLabel }} <span>(last 30 days)</span></div>
                <div class="text-gray-900 text-2xl font-bold" v-text="props.graphs.open.purchase.total.last_30"></div>
                <div class="text-gray-500 text-sm">
                  <span class="font-bold"
                        :class="{'text-teal-500': compareMonths(props.graphs.open.purchase.total) > 0, 'text-red-500': compareMonths(props.graphs.open.purchase.total) < 0}"><span
                    class="fa"
                    :class="{'fa-arrow-up': compareMonths(props.graphs.open.purchase.total) > 0, 'fa-arrow-down': compareMonths(props.graphs.open.purchase.total) < 0}"></span><span
                  ></span><span
                    v-text="compareMonths(props.graphs.open.purchase.total)"></span>%</span>
                  vs last month
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white shadow sm:rounded-md p-3 mb-3">
            <div class="grid gap-2 grid-cols-12 mt-2">
              <div class="col-span-3">
                <div class="bg-red-50 rounded-lg p-4 w-20 h-20">
                  <FolderPlus class="fill-red-600"/>
                </div>
              </div>
              <div class="col-span-9">
                <div class="text-gray-500 text-sm font-normal">{{ closedProjectsLabel }} <span>(last 30 days)</span></div>
                <div class="text-gray-900 text-2xl font-bold" v-text="props.graphs.closed.project.total.last_30"></div>
                <div class="text-gray-500 text-sm">
                  <span class="font-bold"
                        :class="{'text-teal-500': compareMonths(props.graphs.closed.project.total) > 0, 'text-red-500': compareMonths(props.graphs.closed.project.total) < 0}"><span
                    class="fa"
                    :class="{'fa-arrow-up': compareMonths(props.graphs.closed.project.total) > 0, 'fa-arrow-down': compareMonths(props.graphs.closed.project.total) < 0}"></span><span
                  ></span><span
                    v-text="compareMonths(props.graphs.closed.project.total)"></span>%</span>
                  vs last month
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white shadow sm:rounded-md p-3 mb-3">
            <div class="grid gap-2 grid-cols-12 mt-2">
              <div class="col-span-3">
                <div class="bg-red-50 rounded-lg p-4 w-20 h-20">
                  <CartOutline class="fill-red-600 w-full h-full"/>
                </div>
              </div>
              <div class="col-span-9">
                <div class="text-gray-500 text-sm font-normal">{{ closedPurchaseOrdersLabel }} <span>(last 30 days)</span></div>
                <div class="text-gray-900 text-2xl font-bold" v-text="props.graphs.closed.purchase.total.last_30"></div>
                <div class="text-gray-500 text-sm">
                  <span class="font-bold"
                        :class="{'text-teal-500': compareMonths(props.graphs.closed.purchase.total) > 0, 'text-red-500': compareMonths(props.graphs.closed.purchase.total) < 0}"><span
                    class="fa"
                    :class="{'fa-arrow-up': compareMonths(props.graphs.closed.purchase.total) > 0, 'fa-arrow-down': compareMonths(props.graphs.closed.purchase.total) < 0}"></span><span
                  ></span><span
                    v-text="compareMonths(props.graphs.closed.purchase.total)"></span>%</span>
                  vs last month
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="helpers.userIsAdmin(currentUser) || helpers.userIsEmployee(currentUser) || helpers.userIsProjectManager(currentUser)">
            <div class="relative">
              <label class="text-gray-800 text-lg font-semibold">Open Work Orders</label>
              <Link :href="route('admin.workorders.index')"
                    class="text-right text-cyan-500 text-md font-semibold underline absolute right-0">
                View Work Orders
              </Link>
            </div>
            <div class="bg-white shadow sm:rounded-md p-3 mb-3">
              <div class="relative">
                <span class="fa fa-desktop text-gray-800"></span>
                <span class="ml-3 text-gray-800">Current Workorders</span>
                <span v-text="currentWorkOrders.length"
                      class="absolute right-0 text-2xl text-bold text-gray-800"></span>
              </div>
              <div>
                <template v-for="currentOrder in currentWorkOrders">
                  <Link :href="route('admin.workorders.edit', {workorder: currentOrder.ID})"
                        v-text="orderName(currentOrder)" class="block underline text-gray-800"></Link>
                </template>
              </div>
            </div>
            <div class="bg-white shadow sm:rounded-md p-3">
              <div class="relative">
                <span class="fa fa-desktop text-gray-800"></span>
                <span class="ml-3 text-gray-800">Late Workorders</span>
                <span v-text="lateWorkOrders.length" class="absolute right-0 text-2xl text-bold text-gray-800"></span>
              </div>
              <div>
                <template v-for="lateOrder in lateWorkOrders">
                  <Link :href="route('admin.workorders.edit', {workorder: lateOrder.ID})"
                        v-text="orderName(lateOrder)"
                        class="block underline text-gray-800"></Link>
                </template>
              </div>
            </div>

          </div>
        </div>
        <div class="sm:col-span-2">
          <div
            class="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 md:grid-cols-1 sm:grid-cols-1 gap-4 xs:grid-cols-2 mb-3">
            <div>
              <div class="bg-white shadow sm:rounded-md p-3">
                <div class="mb-2">
                  <label class="text-gray-800 text-lg font-semibold align-baseline">{{ projectsBreakdownLabel }}</label>
                </div>
                <ApexStackedColumn :series="[
                  {name: openProjectsSeriesLabel, data: props.graphs.open.project.bar.open}, {name: shippedProjectsSeriesLabel, data: props.graphs.open.project.bar.shipped,}
                ]" :labels="props.graphs.open.project.bar.user"></ApexStackedColumn>
              </div>
            </div>
            <div>
              <div class="bg-white shadow sm:rounded-md p-3">
                <div class="mb-2">
                  <label class="text-gray-800 text-lg font-semibold">{{ purchaseOrdersBreakdownLabel }}</label>
                </div>
                <ApexStackedColumn :series="[
                  {name: openPurchaseOrdersSeriesLabel, data: props.graphs.open.purchase.bar.open}, {name: shippedPurchaseOrdersSeriesLabel, data: props.graphs.open.purchase.bar.shipped,}
                ]" :labels="props.graphs.open.purchase.bar.user"></ApexStackedColumn>
              </div>
            </div>
            <div>
              <div class="bg-white shadow sm:rounded-md p-3">
                <div class="mb-2">
                  <label class="text-gray-800 text-lg font-semibold">{{ projectsDonutTitle }}</label>
                </div>
                <ApexDonut :series="[props.graphs.open.project.pie.open, props.graphs.open.project.pie.shipped]"
                           :labels="projectsDonutLabels"
                           :total-label="projectsDonutTitle"
                ></ApexDonut>
              </div>
            </div>
            <div>
              <div class="bg-white shadow sm:rounded-md p-3">
                <div class="mb-2">
                  <label class="text-gray-800 text-lg font-semibold">{{ purchaseOrdersDonutTitle }}</label>
                </div>
                <ApexDonut :series="[props.graphs.open.purchase.pie.open, props.graphs.open.purchase.pie.shipped]"
                           :labels="purchaseOrdersDonutLabels"
                           :total-label="purchaseOrdersDonutTitle"
                ></ApexDonut>
              </div>
            </div>
          </div>
          <label class="text-gray-800 text-lg font-semibold">Quick Links</label>
          <div class="grid lg:col-span-2 grid-cols-2 gap-1 md:grid-cols-3 md:gap-2 lg:grid-cols-4 lg:gap-3">
            <DashboardBox :href="route('admin.projects.index')" svg="BriefcaseOutline"
                          v-if="isMenuVisible('project_management') && helpers.userHasPermission(currentUser, 'project_management')"
                          :label="quickLinkProjectManagementLabel"></DashboardBox>
            <DashboardBox :href="route('admin.purchase-orders.index')" svg="CartOutline"
                          v-if="isMenuVisible('purchase_orders') && helpers.userHasPermission(currentUser, 'purchase_order')"
                          :label="quickLinkPurchaseOrderLabel"></DashboardBox>
            <DashboardBox href="/content/admin/inventory/inventory.php" svg="ClipboardListOutline"
                          v-if="isMenuVisible('inventory') && helpers.userHasPermission(currentUser, 'inventory')"
                          label="Inventory"></DashboardBox>
            <DashboardBox :href="route('sales.index')" svg="ClipboardCheckOutline"
                          v-if="isMenuVisible('sales') && helpers.userHasPermission(currentUser, 'sales')"
                          label="Forms"></DashboardBox>
            <DashboardBox :href="route('admin.workorders.create')" svg="FolderPlusOutline"
                          v-if="isMenuVisible('new_workorder') && helpers.userHasPermission(currentUser, 'work_order')"
                          label="New Work Order"></DashboardBox>
            <DashboardBox :href="route('admin.quotes.index')" svg="MessagesOutline"
                          v-if="isMenuVisible('quotes') && helpers.userHasPermission(currentUser, 'generate_request')"
                          label="Quotes"></DashboardBox>
            <DashboardBox :href="route('admin.samples.index')" svg="DatabaseOutline"
                          v-if="isMenuVisible('sample_database') && helpers.userHasPermission(currentUser, 'sample_database')"
                          label="Sample Database"></DashboardBox>
            <DashboardBox :href="route('internal.office-calendar.index')" svg="CalendarMonthOutline"
                          v-if="isMenuVisible('calendar') && helpers.userHasPermission(currentUser, 'calendar')"
                          label="Calendar"></DashboardBox>
          </div>

        </div>
      </div>

    </div>
  </AppLayout>
</template>
